// # 세션 — REST·소켓·저장소를 한 덩어리로 묶는 곳
//
// 엔진(`sync.ts`)은 "티켓을 어떻게 받는지", "갭을 어디서 메우는지" 를 모른다.
// 저장소(`Store.ts`)는 사건이 어디서 오는지 모른다. 그 둘을 잇는 접착제가 여기고,
// **앱 진입점이 아는 것은 이 파일 하나**다.
//
// ## 시작 순서가 곧 정확성이다
// 1. REST 로 채널 목록과 읽음 상태를 받아 저장소에 심는다 — 화면이 처음부터 숫자를 보인다.
// 2. 그 `last` 을 엔진 커서로 심는다 — 붙자마자 "여기까지 가졌다" 고 말할 수 있다.
// 3. 그제야 소켓을 연다.
// 순서를 바꾸면 커서 없이 붙어 서버가 "메울 것 없음" 이라 답하고, 그 사이 온 메시지는 없다.
//
// ## 백필은 사건으로 흘린다
// REST 로 받은 메시지를 저장소에 따로 넣는 길을 만들지 않는다. 한 건씩 `Message` 사건으로
// 바꿔 같은 문으로 넣는다 — 상태를 바꾸는 길이 하나면 "왜 여기만 다르지" 가 없다.

import type { ChannelId, Event, Seq, UserId } from '@maru/protocol'
import {
  type ApiConfig,
  ApiError,
  type Channel,
  type Handout,
  type LinkCard,
  MaruApi,
} from './api.ts'
import { Lookup } from './lookup.ts'
import { Polls } from './polls.ts'
import { Reactions } from './reactions.ts'
import { chat_store } from './store.ts'
import { SyncEngine } from './sync.ts'
import { Timeline } from './timeline.ts'

export interface SessionConfig extends ApiConfig {
  /** 생략하면 `base_url` 에서 만든다. `''` 이면 현재 페이지의 오리진이다. */
  socket_url?: string
  /** 시험용 시계. */
  now?: () => number
}

/**
 * "쓰는 중" 을 같은 채널에 이보다 자주 보내지 않는다.
 *
 * 글자마다 보내면 한 사람이 초당 열 번 서버를 두드린다. 받는 쪽 TTL(4초)보다
 * 짧게만 보내면 불이 꺼지지 않는다 — 그래서 3초.
 */
export const TYPING_EVERY_MS = 3000

/**
 * 첨부 받을곳을 다시 묻는 간격. 서버는 서명 시각을 5분 눈금으로 내려 맞추고 10분을 살린다
 * (`VIEW_BUCKET`·`VIEW_TTL`) — 가장 늦게 받은 주소도 5분은 산다. 그보다 짧게 묻고, 같은
 * 구간이면 같은 주소가 와서 `Lookup` 이 아무것도 알리지 않는다(줄도 그림도 그대로).
 */
export const FILE_SLOT_TTL_MS = 4 * 60_000

/** 얼굴 주소를 다시 묻는 간격. 서버 주소는 최소 90분 산다(`FACE_TTL`·`FACE_BUCKET`). */
export const FACE_TTL_MS = 20 * 60_000

/** 화면이 보는 것. 껍데기는 이것만 안다. */
export interface ScreenState {
  conn: 'Dropped' | 'Connecting' | 'Attached' | 'catching_up'
  badge: number
}

/**
 * 껍데기가 기대는 최소 표면. `@maru/ui` 는 이 모양만 알고 이 파일을 모른다 —
 * 시험에서는 이 네 함수를 가짜로 채운다.
 */
export interface Session {
  /**
   * 진실을 가져오는 문. 껍데기는 이걸로 읽고 쓴다.
   *
   * 세션이 감춰 두면 화면이 자기 `fetch` 를 만들게 되고, 토큰 갱신이 닿지 않는
   * 두 번째 경로가 생긴다. 문은 하나여야 한다.
   */
  api: MaruApi
  /** 연 채널의 본문. 사건이 오면 빠진 구간만 채운다(`timeline.ts`). */
  timeline: Timeline
  /** 보이는 줄의 이모지 집계(`reactions.ts`). */
  reactions: Reactions
  /** 보이는 줄에 매달린 투표(`polls.ts`). */
  polls: Polls
  /** 첨부 id → 받을 곳. 서명이 60초라 45초마다 다시 받는다. */
  files: Lookup<Handout>
  /** 링크 주소 → 카드. 못 펼친 주소는 `null`. */
  cards: Lookup<LinkCard>
  /**
   * 사람 번호(글자) → 얼굴 사진 주소. 사진 없는 사람은 `null` — 화면은 이름 글자로 그린다.
   * 주소는 한 시간 반은 살므로 20분마다만 다시 묻는다.
   */
  faces: Lookup<string>
  /**
   * 새로 연 채널을 캐시와 커서에 심는다. 없으면 그 채널의 첫 사건이 "구멍" 으로
   * 보여 엔진이 1번부터 전부 받으려 든다.
   */
  adopt(channel: Channel): void
  drop(channel: ChannelId): void
  /** 소켓 사건을 엿본다(알림·명단 새로 고침). 저장소를 거치지 않는 것만 여기서 듣는다. */
  on(listen: (e: Event) => void): () => void
  start(): Promise<void>
  stop(): void
  /** 상태가 바뀔 때마다 부른다. 해제 함수를 돌려준다. `useSyncExternalStore` 꼴. */
  subscribe(listen: () => void): () => void
  /** 같은 상태면 **같은 객체**를 돌려준다. 안 그러면 React 가 무한히 다시 그린다. */
  scan(): ScreenState
  /**
   * "이 채널에 쓰는 중". 글자마다 불러도 된다 — 채널마다 3초에 한 번만 나간다.
   * 소켓이 닫혀 있으면 조용히 버린다. 쓰는 중 표시는 잃어도 되는 정보다.
   */
  typing(channel: ChannelId): void
  /**
   * "이 채널 캔버스의 여기를 보고 있다". `at` 이 `null` 이면 보기만 한다, `left` 면 판을 닫았다.
   * 조르기는 부르는 쪽(`CanvasSync`)이 한다. 소켓이 닫혀 있으면 버린다 — 맥박이 곧 다시 보낸다.
   */
  canvas_here(channel: ChannelId, at: number | null, end: number | null, left?: boolean): void
}

export function session_open(config: SessionConfig): Session {
  const api = new MaruApi(config)
  const save = chat_store
  const timeline = new Timeline(api)
  const reactions = new Reactions(api)
  const polls = new Polls(api)
  const files = new Lookup<Handout>({
    fetch_many: (ids) => handouts_of(ids, (id) => api.attachment(id)),
    ttl_ms: FILE_SLOT_TTL_MS,
    batch_max: 20,
  })
  const cards = new Lookup<LinkCard>({
    fetch_many: async (urls) => new Map((await api.unfurl(urls)).map((c) => [c.url, c])),
    ttl_ms: 10 * 60_000,
    batch_max: 10,
  })

  const faces = new Lookup<string>({
    fetch_many: async (keys) => {
      const got = await api.faces(keys.map((k) => Number(k) as UserId))
      return new Map(got.map((f) => [String(f.user), f.url]))
    },
    ttl_ms: FACE_TTL_MS,
    batch_max: 100,
  })

  const engine = new SyncEngine({
    socket_url: config.socket_url ?? socket_url_of(config.base_url),
    ticket: () => api.ticket(),
    backfill: async (channel, since, upto) => {
      const got = await api.message_range(channel, since, upto)
      for (const m of got) {
        save.getState().on_event({ t: 'message', channel, seq: m.seq, author: m.author })
      }
      // 같은 구간을 타임라인이 또 묻지 않게 받은 것을 그대로 건넨다.
      timeline.absorb(channel, got)
    },
    stale: (channels) => {
      for (const c of channels) {
        void timeline.refresh(c)
        reactions.refresh(c)
        polls.refresh(c)
      }
    },
    apply: (e: Event) => {
      save.getState().on_event(e)
      timeline.on_event(e)
      reactions.on_event(e)
      polls.on_event(e)
      for (const l of taps) l(e)
    },
    changed: (s) => save.getState().conn_changed(s.conn),
  })

  // 훑기가 매번 새 객체를 만들면 React 가 "바뀌었다" 고 보고 끝없이 그린다.
  // 저장소 상태 객체는 바뀔 때만 새로 만들어지므로 그것을 키로 메모한다.
  let last_key: unknown = null
  let last_value: ScreenState = { conn: 'Dropped', badge: 0 }

  const now = config.now ?? (() => Date.now())
  const typed_at = new Map<ChannelId, number>()
  const taps = new Set<(e: Event) => void>()

  return {
    api: api,
    timeline: timeline,
    reactions: reactions,
    polls: polls,
    files: files,
    cards: cards,
    faces: faces,
    // 받아 오기(커서 심기)도 엔진의 붙기 세대 안에서 한다 — 그 사이 `stop` 이 불리면(화면이 내려감·
    // StrictMode) 늦게 끝난 시작은 붙지 않는다(3차 F128).
    start: () =>
      engine.start(async () => {
        const [channels, read_states] = await Promise.all([api.my_channels(), api.read_state()])
        const read_state = new Map(read_states.map((r) => [r.channel, r]))
        save.getState().seed_channel(
          channels.map((c) => ({
            channel: c.id,
            last: c.last,
            read_upto: read_state.get(c.id)?.read_upto ?? (0 as Seq),
            mention_count: read_state.get(c.id)?.mention_count ?? 0,
          })),
        )
        engine.seed_cursor(channels.map((c) => ({ channel: c.id as ChannelId, have: c.last })))
      }),
    stop: () => engine.stop(),
    subscribe: (listen) => save.subscribe(listen),
    adopt(c) {
      save.getState().add_channel(c.id, c.last)
      if (!engine.status.cursor.has(c.id)) engine.seed_cursor([{ channel: c.id, have: c.last }])
      // 소켓은 붙을 때(`resume`) 가진 방만 구독했다. 붙어 있는 동안 생긴 방(새 채널·새 대화·
      // 리마인더가 연 나와의 대화)은 여기서 구독해야 그 방의 새 글이 실시간으로 온다 — 안 하면
      // 다시 붙기 전까지 조용하다(3차 F153). 끊겨 있으면 못 보내도 된다: 다시 붙을 때 커서에 있다.
      engine.send({ op: 'subscribe', channel: c.id })
    },
    drop: (c) => save.getState().drop_channel(c),
    on(listen) {
      taps.add(listen)
      return () => void taps.delete(listen)
    },
    typing(channel) {
      const t = now()
      const before = typed_at.get(channel)
      if (before !== undefined && t - before < TYPING_EVERY_MS) return
      // 못 보냈으면 시각을 남기지 않는다 — 붙자마자 다음 글자에 바로 나가게.
      if (engine.send({ op: 'typing', channel })) typed_at.set(channel, t)
    },
    canvas_here(channel, at, end, left) {
      engine.send(
        left === true
          ? { op: 'canvas_here', channel, at: null, end: null, left }
          : { op: 'canvas_here', channel, at, end },
      )
    },
    scan() {
      const status = save.getState()
      if (status !== last_key) {
        last_key = status
        last_value = { conn: status.conn, badge: status.badge_total() }
      }
      return last_value
    },
  }
}

/** `http(s)://host` → `ws(s)://host/v1/socket`. 빈 문자열은 현재 오리진. */
export function socket_url_of(base_url: string): string {
  if (base_url === '') {
    const l = globalThis.location
    return `${l.protocol === 'https:' ? 'wss' : 'ws'}://${l.host}/v1/socket`
  }
  return `${base_url.replace(/^http/, 'ws')}/v1/socket`
}

/**
 * 첨부 여럿을 한 틱에 묻는다(첨부는 하나씩 묻는 길뿐이다). **404 만 "없다"** 로 적는다 — 서버가 잠깐
 * 끊기거나 5xx 를 낸 것까지 없음으로 적으면 멀쩡한 파일이 "파일을 찾을 수 없어요" 로 다음 새로 고침까지
 * 선다(3차 F242). 그 밖의 실패는 던져 `Lookup` 이 적지 않고 다음 `need` 에 다시 묻게 한다.
 */
export async function handouts_of(
  ids: readonly string[],
  get: (id: string) => Promise<Handout>,
): Promise<Map<string, Handout>> {
  const got = await Promise.all(
    ids.map((id) =>
      get(id).catch((e: unknown) => {
        if (e instanceof ApiError && e.status === 404) return null
        throw e
      }),
    ),
  )
  const m = new Map<string, Handout>()
  got.forEach((h, i) => {
    const id = ids[i]
    if (h !== null && id !== undefined) m.set(id, h)
  })
  return m
}
